/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import TableIndeterminateCheckbox from 'components/Ui/Tanstack/Actions/TableIndeterminateCheckbox'

export const MASS_ACTIONS_SELECT_COLUMN_ID = 'select'

/**
 * Builds the auto-injected selection column used by `useTable` when row
 * selection is enabled. Centralized here so the column id, sizing, and
 * UX (header indeterminate + per-row checkbox) stay consistent across
 * every Tanstack table in the app.
 */
export const buildSelectColumn = <T,>(): ColumnDef<T, any> => ({
    id: MASS_ACTIONS_SELECT_COLUMN_ID,
    enableSorting: false,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableResizing: false,
    meta: { className: 'w-10 shrink-0' },
    header: ({ table }) => {
        // Plain div wrapper (NOT a <label> — a label that wraps the input
        // makes the browser re-dispatch a click on the input, which can
        // double-toggle the checkbox and effectively cancel the change).
        // The div gives a larger click target and stops propagation so the
        // parent header sort/filter handlers never receive the event.
        const stop = (event: React.SyntheticEvent) => event.stopPropagation()
        const toggle = table.getToggleAllPageRowsSelectedHandler()
        return (
            <div
                className="flex items-center justify-center h-full w-full cursor-pointer select-none"
                data-row-selection-toggle="true"
                onMouseDown={stop}
                onClick={(event) => {
                    event.stopPropagation()
                    if (event.target !== event.currentTarget) {
                        return
                    }
                    table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected())
                }}
            >
                <TableIndeterminateCheckbox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={toggle}
                    onClick={stop}
                    data-row-selection-toggle="true"
                />
            </div>
        )
    },
    cell: ({ row }) => {
        const stop = (event: React.SyntheticEvent) => event.stopPropagation()
        const toggle = row.getToggleSelectedHandler()
        return (
            <div
                className="flex items-center justify-center h-full w-full cursor-pointer select-none"
                data-row-selection-toggle="true"
                onMouseDown={stop}
                onClick={(event) => {
                    event.stopPropagation()
                    if (event.target !== event.currentTarget) {
                        return
                    }
                    if (!row.getCanSelect()) {
                        return
                    }
                    row.toggleSelected(!row.getIsSelected())
                }}
            >
                <TableIndeterminateCheckbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={toggle}
                    onClick={stop}
                    data-row-selection-toggle="true"
                />
            </div>
        )
    },
})
